import React from "react";
import { Menu, ChevronRight, Star, Shield, Check } from "lucide-react";
import SettingsIcon from "../components/SettingsIcon";
import ChartIcon from "../components/ChartIcon";
import CursorIcon from "../components/CursorIcon";

interface ServiceCardProps {
  icon: React.ComponentType<{ size?: number; color?: string; className?: string }>;
  title: string;
  description: string;
  iconColor: string;
}

interface TestimonialCardProps {
  author: string;
  company: string;
  text: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

interface Service extends ServiceCardProps {}
interface Testimonial extends TestimonialCardProps {}

const Header: React.FC = () => (
  <header className="fixed bg-base-100 top-0 w-full z-50 shadow-sm">
    <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-16">
      <img src="logo.svg" alt="Logo" className="w-60" />

      <nav className="hidden md:flex items-center gap-8">
        <a href="#about" className="hover:text-primary">
          Über uns
        </a>
        <a href="#services" className="hover:text-primary">
          Leistungen
        </a>
        <a href="#benefits" className="hover:text-primary">
          Vorteile
        </a>
        <a href="#faq" className="hover:text-primary">
          FAQ
        </a>
      </nav>

      <div className="hidden md:block">
        <button className="btn btn-primary" onClick={() => (window.location.href = "mailto:info@applicationflow.de")}>
          Kontaktiere uns!
        </button>
      </div>

      <button className="md:hidden">
        <Menu className="h-6 w-6" />
      </button>
    </div>
  </header>
);

const ServiceCard: React.FC<ServiceCardProps> = ({ icon: Icon, title, description, iconColor }) => (
  <div className="card bg-base-100 shadow-xl">
    <div className="card-body items-center text-center">
      <Icon className="h-12 w-12 mb-4" color={iconColor} />
      <h3 className="card-title mb-2">{title}</h3>
      <div className="divider"></div>
      <p className="text-base-content/70">{description}</p>
    </div>
  </div>
);

const TestimonialCard: React.FC<TestimonialCardProps> = ({ author, company, text }) => (
  <div className="card bg-base-100 shadow-xl">
    <div className="card-body">
      <div className="flex gap-2 mb-2">
        {[...Array(5)].map((_, i) => (
          <Star key={i} className="h-4 w-4 fill-primary text-primary" />
        ))}
      </div>
      <p className="mb-4">{text}</p>
      <div className="flex items-center gap-2">
        <div className="avatar placeholder">
          <div className="bg-neutral text-neutral-content rounded-full w-8">
            <span className="text-xs">{author[0]}</span>
          </div>
        </div>
        <div>
          <p className="font-bold">{author}</p>
          <p className="text-sm text-base-content/70">{company}</p>
        </div>
      </div>
    </div>
  </div>
);

const FAQItem: React.FC<FAQItem> = ({ question, answer }) => (
  <div className="collapse collapse-arrow bg-base-100">
    <input type="checkbox" />
    <div className="collapse-title text-lg font-medium">{question}</div>
    <div className="collapse-content">
      <p>{answer}</p>
    </div>
  </div>
);

const BenefitItem: React.FC<{ title: string; description: string }> = ({ title, description }) => (
  <div className="flex gap-4 items-start">
    <div className="rounded-full bg-green-500 p-1 mt-1">
      <Check className="h-4 w-4 text-white" />
    </div>
    <div>
      <h4 className="font-bold text-lg">{title}</h4>
      <p className="text-base-content/70">{description}</p>
    </div>
  </div>
);

const LandingPage: React.FC = () => {
  const services = [
    {
      icon: SettingsIcon,
      title: "Prozessoptimierung & Effizienzsteigerung",
      description: "Automatisieren und optimieren Sie Ihre Abläufe für maximale Produktivität und klar strukturierte Prozesse.",
      iconColor: "#000000",
    },
    {
      icon: ChartIcon,
      title: "Datenanalyse & strategische Weiterentwicklung",
      description:
        "Von Integrationen bereits bestehender Lösungen bis hin zu maßgeschneiderter Software! Wir planen und entwicklen auf Sie zugeschnittene Lösungen um Ihre Unternehmung noch effizienter und robuster zu gestalten.",
      iconColor: "#22C55E", // green-500
    },
    {
      icon: CursorIcon,
      title: "Marketinglösungen für nachhaltiges Wachstum",
      description: "Setzen Sie auf datengetriebene Marketingstrategien, um gezielt zu wachsen und Ihre Zielgruppe effektiv zu erreichen.",
      iconColor: "#F97316", // orange-500
    },
  ];

  const benefits = [
    {
      title: "Effizienz steigern",
      description: "Automatisierte Prozesse für maximale Produktivität.",
    },
    {
      title: "Zukunftssicher digitalisieren",
      description: "Maßgeschneiderte IT- und Marketing Lösungen für nachhaltiges Wachstum.",
    },
    {
      title: "Fundierte Entscheidungen treffen",
      description: "Datenanalyse für bessere Strategien und gezieltes Marketing.",
    },
  ];

  const testimonials: Testimonial[] = [
    {
      author: "Leonardo Dilßner",
      company: "Systoms GmbH",
      text: "Hervorragende Zusammenarbeit und exzellente Ergebnisse.",
    },
    {
      author: "Leonardo Dilßner",
      company: "StudioOS GmbH",
      text: "Hervorragende Zusammenarbeit und exzellente Ergebnisse.",
    },
    {
      author: "Okan Koca",
      company: "Global4ce GmbH",
      text: "Hervorragende Zusammenarbeit und exzellente Ergebnisse.",
    },
    {
      author: "Jonathan Handt",
      company: "HDT Systems GmbH",
      text: "Hervorragende Zusammenarbeit und exzellente Ergebnisse.",
    },
    {
      author: "Erik Blumenstein",
      company: "HDT Systems GmbH",
      text: "Hervorragende Zusammenarbeit und exzellente Ergebnisse.",
    },
  ];

  const faqs: FAQItem[] = [
    {
      question: "Welche Unternehmen profitieren von Ihren Lösungen?",
      answer: "Besonders Unternehmen im Dienstleistungssektor.",
    },
    {
      question: "Bieten Sie auch langfristige Betreuung an?",
      answer: "Ja, wenn gewünscht bieten wir fortlaufende Unterstützung nach der Implementierung.",
    },
    {
      question: "Ich weiß nicht was ich überhaupt bei mir optimieren kann, können Sie helfen?",
      answer: "Ja, kontaktieren Sie uns über das Formular. In einem ersten unverbindlichen Gespräch analysieren wir Ihr Unternehmen und finden gemeinsam die beste Option.",
    },
    {
      question: "Arbeiten Sie mit Unternehmen jeder Größe zusammen?",
      answer: "Ja, angefangen von KMU's bis zu Franchiseunternehmen.",
    },
  ];

  return (
    <div className="min-h-screen">
      <Header />
      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 pt-24 lg:pt-32 pb-16">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
          <div className="flex-1 lg:max-w-[45%]">
            <h1 className="text-3xl font-bold mb-6">
              Ihre Prozesse, optimiert.
              <br />
              Ihre Zukunft, digital.
              <br />
              Ihre Lösung, bei uns.
              <br />
            </h1>
            <p className="text-md mb-8 text-base-content/40 break-words">
              Mit maßgeschneiderten Lösungen für Prozessoptimierungen und Digitalisierung machen wir Ihr Unternehmen fit für die Zukunft - für nachhaltiges Wachstum und dauerhaften Erfolg!
            </p>
            <button className="btn btn-primary btn-lg" onClick={() => (window.location.href = "mailto:info@applicationflow.de")}>
              Jetzt digitale Transformation starten
              <ChevronRight className="ml-2" />
            </button>
          </div>
          <div className="flex-1 lg:max-w-[50%] w-full">
            <div className="rounded-lg bg-base-100 aspect-video w-full flex items-center justify-end">
              <img className="w-72 h-72 object-cover max-w-lg" alt="Person an einem Bürotisch" src="hero_section_image.png" />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="bg-neutral py-16" id="services">
        <div className="max-w-7xl mx-auto px-4">
          <p className="text-primary text-center mb-2">Unsere Kernleistungen</p>
          <h2 className="text-4xl font-bold text-center mb-48">Mit nur drei Schritten Richtung Zielgerade!</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {services.map((service, i) => (
              <ServiceCard key={i} {...service} />
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16" id="cases">
        <div className="container mx-auto px-4">
          {/* <h2 className="text-4xl font-bold text-center mb-12">Was unsere Kunden sagen</h2> */}
          <div className="carousel carousel-center max-w-md mx-auto gap-4">
            {/* {testimonials.map((testimonial, i) => (
              <div key={i} className="carousel-item w-full">
                <TestimonialCard {...testimonial} />
              </div>
            ))} */}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-neutral py-16" id="benefits">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-16">Ihre Suche nach einem Partner für Digitales hat ein Ende!</h2>

          <div className="grid lg:grid-cols-2 gap-12 items-center mb-24">
            {/* Left side with image */}
            <div>
              <img src="mid_section_image.png" alt="Team working" className="rounded-lg w-full" />
            </div>

            {/* Right side with content */}
            <div>
              <h3 className="text-2xl font-bold mb-6">Ohne Umstände zum digitalen Vorreiter!</h3>
              <p className="text-base-content/70 mb-8">
                Für alle Arten von Unternehmen ist es wichtig, Daten zu analysieren, Prozesse zu digitalisieren und beim Kunden präsent zu sein. Selbst wenn Ihr Unternehmen "ganz gut läuft", wird das Hinzufügen von mehr
                Effizienz, Wachstum und Einfachheit mit sich bringen. Dadurch wird Ihr Unternehmen für Mitarbeiter, bestehende und potenzielle Kunden sowie Partner und nicht zuletzt für Sie selbst, attraktiver.
              </p>

              {/* Checkmark list */}
              <div className="space-y-6">
                {benefits.map((benefit, index) => (
                  <BenefitItem key={index} {...benefit} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="bg-white py-16" id="about">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left side with title and CTA */}
            <div>
              <h2 className="text-4xl font-bold mb-64">Über Application Flow</h2>
              <button className="btn btn-primary btn-lg" onClick={() => (window.location.href = "mailto:info@applicationflow.de")}>
                Jetzt digitale Transformation starten
                <ChevronRight className="ml-2" />
              </button>
            </div>

            {/* Right side with 2x2 grid */}
            <div className="grid grid-cols-2 gap-6">
              {/* Top row */}
              <div>
                <div className="card-body">
                  <h4 className="font-bold text-lg mb-2">Bestehen</h4>
                  <p className="text-base-content/70">Als klassisches Start-up 2023 gegründet - gestützt von nachgewiesenen Geschäftserfolgen</p>
                </div>
              </div>

              <div>
                <div className="card-body">
                  <h4 className="font-bold text-lg mb-2">Erfahrung</h4>
                  <p className="text-base-content/70">Mit über 10 Jahren Erfahrung bringen wir fundiertes Know-how in unser junges Unternehmen ein</p>
                </div>
              </div>

              {/* Bottom row */}
              <div>
                <div className="card-body">
                  <h4 className="font-bold text-lg mb-2">Innovative Lösungen</h4>
                  <p className="text-base-content/70">Wir setzen auf modernste Technologien und maßgeschneiderte Ansätze, um Ihre Prozesse zu optimieren</p>
                </div>
              </div>

              <div>
                <div className="card-body">
                  <h4 className="font-bold text-lg mb-2">100% Verantwortung</h4>
                  <p className="text-base-content/70">Unsere Unternehmen hat seinen Sitz in Heidelberg, Deutschland. Unabhängig wo Sie sich auf der Welt befinden- wir arbeiten zu 100% digital</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-neutral py-16" id="faq">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12">Häufig gestellte Fragen</h2>
          <div className="max-w-2xl mx-auto space-y-4">
            {faqs.map((faq, i) => (
              <FAQItem key={i} {...faq} />
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center">
            {/* Left side - Impressum link */}
            <div>
              <a href="/impressum" className="link link-hover text-gray-600">
                Impressum
              </a>
            </div>

            {/* Right side - Social Icons */}
            <div className="flex gap-4">
              <a href="https://www.facebook.com/profile.php?id=100090199836006" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-blue-600">
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
                </svg>
              </a>
              <a href="https://www.instagram.com/applicationflow.de/" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-pink-600">
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2c2.717 0 3.056.01 4.122.06 1.065.05 1.79.217 2.428.465.66.254 1.216.598 1.772 1.153a4.908 4.908 0 0 1 1.153 1.772c.247.637.415 1.363.465 2.428.047 1.066.06 1.405.06 4.122 0 2.717-.01 3.056-.06 4.122-.05 1.065-.218 1.79-.465 2.428a4.883 4.883 0 0 1-1.153 1.772 4.915 4.915 0 0 1-1.772 1.153c-.637.247-1.363.415-2.428.465-1.066.047-1.405.06-4.122.06-2.717 0-3.056-.01-4.122-.06-1.065-.05-1.79-.218-2.428-.465a4.89 4.89 0 0 1-1.772-1.153 4.904 4.904 0 0 1-1.153-1.772c-.248-.637-.415-1.363-.465-2.428C2.013 15.056 2 14.717 2 12c0-2.717.01-3.056.06-4.122.05-1.066.217-1.79.465-2.428a4.88 4.88 0 0 1 1.153-1.772A4.897 4.897 0 0 1 5.45 2.525c.638-.248 1.362-.415 2.428-.465C8.944 2.013 9.283 2 12 2zm0 3.25a6.75 6.75 0 1 0 0 13.5 6.75 6.75 0 0 0 0-13.5zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.5-11.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
